import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaHome, FaUser, FaGraduationCap, FaTicketAlt, FaCogs,
  FaProjectDiagram, FaChartPie, FaClipboardCheck, FaUsers,
  FaCalendar, FaBars, FaTimes, FaQrcode, FaPowerOff
} from 'react-icons/fa';

const API_ROLE = 'https://dev.sliitleo.org/Backend/user_role.php';

const menuItemsConfig = [
  { icon: <FaHome/>,             label: 'Home',             route: '/home',               exact: true },
  { icon: <FaUser/>,             label: 'My Profile',       route: '/profile',            exact: true },
  { icon: <FaGraduationCap/>,    label: 'Leo Education',    route: '/education',          exact: true },
  { icon: <FaTicketAlt/>,        label: 'My Tickets',       route: '/tickets',            exact: true },
  { icon: <FaCalendar/>,         label: 'Club Calendar',    route: '/club-calander',      exact: true },
  { separator: true },
  { icon: <FaCogs/>,             label: 'Admin Console',    route: '/admin',              exact: false },
  { icon: <FaProjectDiagram/>,   label: 'Club Projects',    route: '/club-projects',      exact: true },
  { icon: <FaChartPie/>,         label: 'Club Dashboard',   route: '/club-dashboard',     exact: true },
  { icon: <FaClipboardCheck/>,   label: 'Reporting Hub',    route: '/reporting-hub',      exact: true },
  { icon: <FaUsers/>,            label: 'Installation',     route: '/installation',       badge: null, exact: true },
  { icon: <FaGraduationCap/>,    label: 'Education Admin',  route: '/education-statistics', exact: true },
  { icon: <FaQrcode/>,           label: 'Attendance',       route: '/attendance',         exact: true },
];

const titleCycle = ['LEO CLUB OF SLIIT', 'DISTRICT 306 D6', 'CLUB PORTAL 4.0'];
const slideVariants = {
  enter:  { y: 10, opacity: 0, position: 'absolute', filter: 'blur(2px)' },
  center: { y: 0,  opacity: 1, position: 'relative', filter: 'blur(0)' },
  exit:   { y: -10, opacity: 0, position: 'absolute', filter: 'blur(2px)' },
};

// helpers
const readStoredUser = () => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } };
const normalizeRole = (role) =>
  String(role || '')
    .toLowerCase()
    .replace(/[\.\-_/]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

// mid-tier roles list (unchanged)
const MID_ROLES = new Set([
  'secretary',
  'treasurer',
  'co asst treasurer',
  'co asst secretary',
  'co assistant treasurer',
  'installation sub committee member',
  'co assistant secretary',
]);

const MID_ROLE_ALLOWED_LABELS = new Set([
  'Home',
  'My Profile',
  'Leo Education',
  'My Tickets',
  'Club Calendar',
  'Reporting Hub',
  'Club Projects',
  'Installation',
]);

// NEW: restricted roles list you gave — show member-only by default
const RESTRICTED_ROLES = new Set([
  'chief editor',
  'president program coordinator',
  'club performence evaluator',      // keep given spelling
  'club performance evaluator',      // and a safer variant just in case
  'club director',
  'team head',
  'activity director',
  'past president',
  'past board member',
  'it department',
  'installation sub committee member',  // normalized “sub-committee”
  'reporting sub committee',
  'committee member',
]);

// NEW: roles that also see Installation
const SEE_INSTALLATION_ROLES = new Set([
  'installation sub committee member',
  'president program coordinator',
  'club performence evaluator',
  'club performance evaluator',
  'secretary',
  'treasurer',
]);

// NEW: roles that also see Reporting Hub
const SEE_REPORTING_HUB_ROLES = new Set([
  'club performence evaluator',
  'club performance evaluator',
  'reporting sub committee',
]);

const baseMemberMenu = () => {
  const cut = menuItemsConfig.findIndex(i => i.separator);
  return cut === -1 ? [...menuItemsConfig] : menuItemsConfig.slice(0, cut);
};

const findItem = (label) => menuItemsConfig.find(i => i.label === label);

// main builder
const buildMenuByRole = (role) => {
  const norm = normalizeRole(role);

  // Member only
  if (norm === 'club member' || norm === 'member') {
    return baseMemberMenu();
  }

  // Your “restricted list”: member-only + specific extras
  if (RESTRICTED_ROLES.has(norm)) {
    const list = baseMemberMenu();

    if (SEE_INSTALLATION_ROLES.has(norm)) {
      const installation = findItem('Installation');
      if (installation) list.push(installation);
    }
    if (SEE_REPORTING_HUB_ROLES.has(norm)) {
      const reportHub = findItem('Reporting Hub');
      if (reportHub) list.push(reportHub);
    }
    return list;
  }

  // Mid-tier (secretariat/finance)
  if (MID_ROLES.has(norm)) {
    return menuItemsConfig.filter(
      item => !item.separator && MID_ROLE_ALLOWED_LABELS.has(item.label)
    );
  }

  // Everyone else (leaders/admins) gets full menu
  return menuItemsConfig;
};

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [menuItems, setMenuItems] = useState(buildMenuByRole('Club Member'));
  const [role, setRole] = useState('Club Member');

  const [isOpen, setIsOpen] = useState(() => window.innerWidth >= 1024);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [expandedItems, setExpandedItems] = useState({});
  const [titleIndex, setTitleIndex] = useState(0);

  // fetch role
  useEffect(() => {
    const stored = readStoredUser();
    const payload =
      stored.SLIITLeoID ? { id: stored.SLIITLeoID } :
      (stored.email || stored.Email) ? { email: stored.email || stored.Email } :
      null;

    if (!payload) {
      const r = stored.role || 'Club Member';
      setRole(r);
      setMenuItems(buildMenuByRole(r));
      return;
    }

    (async () => {
      try {
        const res = await fetch(API_ROLE, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        const r = (data?.success && (data.user?.role || 'Club Member')) || (stored.role || 'Club Member');
        setRole(r);
        setMenuItems(buildMenuByRole(r));
        localStorage.setItem('user', JSON.stringify({ ...stored, role: r }));
      } catch {
        const r = stored.role || 'Club Member';
        setRole(r);
        setMenuItems(buildMenuByRole(r));
      }
    })();
  }, []);

  const menuItemsWithState = useMemo(
    () =>
      menuItems.map((item) => ({
        ...item,
        isActive:
          item.route &&
          (item.exact ? location.pathname === item.route : location.pathname.startsWith(item.route)),
      })),
    [menuItems, location.pathname]
  );

  // responsive open/close
  useEffect(() => {
    let t;
    const onResize = () => {
      clearTimeout(t);
      t = setTimeout(() => {
        const mobile = window.innerWidth < 1024;
        setIsMobile(mobile);
        setIsOpen(!mobile);
      }, 100);
    };
    window.addEventListener('resize', onResize);
    onResize();
    return () => {
      window.removeEventListener('resize', onResize);
      clearTimeout(t);
    };
  }, []);

  // installation badge (if present in current menu)
  useEffect(() => {
    const run = async () => {
      try {
        const res = await fetch('https://dev.sliitleo.org/Backend/installation_total.php');
        if (!res.ok) throw new Error('Bad network');
        const data = await res.json();
        if (typeof data.total === 'number') {
          setMenuItems((items) =>
            items.map((it) => (it.label === 'Installation' ? { ...it, badge: data.total } : it))
          );
        }
      } catch (e) {
        console.error('Failed to fetch installation total:', e);
      }
    };
    run();
  }, []);

  // rotating title
  useEffect(() => {
    const id = setInterval(() => setTitleIndex((i) => (i + 1) % titleCycle.length), 2600);
    return () => clearInterval(id);
  }, []);

  const toggleMenu   = useCallback(() => setIsOpen((p) => !p), []);
  const toggleExpand = useCallback((label) => setExpandedItems((prev) => ({ ...prev, [label]: !prev[label] })), []);
  const handleLogout = useCallback(() => {
    try { localStorage.clear(); sessionStorage.clear(); } catch {}
    navigate('/login');
    if (isMobile) setIsOpen(false);
  }, [navigate, isMobile]);

  return (
    <>
      {isMobile && !isOpen && (
        <button
          onClick={toggleMenu}
          className="fixed top-4 left-4 z-40 p-2 rounded-lg text-white md:hidden"
          aria-label="Open menu"
          style={{
            background: 'rgba(16,26,51,.92)',
            border: '1px solid rgba(255,255,255,.10)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <FaBars size={18} />
        </button>
      )}

      <AnimatePresence>
        {isMobile && isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.6 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="fixed inset-0 bg-black z-30 md:hidden"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />
        )}
      </AnimatePresence>

      <motion.aside
        initial={{ x: isMobile ? -320 : 0 }}
        animate={{
          x: isOpen ? 0 : -320,
          opacity: 1,
          display: isMobile ? 'block' : 'flex',
        }}
        transition={{ type: 'spring', stiffness: 520, damping: 38 }}
        className="fixed top-0 left-0 h-screen w-64 text-white z-40 flex flex-col"
        aria-label="Main navigation"
        style={{
          background: 'linear-gradient(180deg,#0F1830 0%, #0D172C 60%, #0E1426 100%)',
          borderRight: '1px solid rgba(255,255,255,.06)',
          boxShadow: '0 12px 40px rgba(0,0,0,.45)'
        }}
      >
        {isMobile && (
          <button
            onClick={() => setIsOpen(false)}
            className="absolute top-3 right-3 p-2 rounded-lg text-white/90 hover:text-white"
            aria-label="Close menu"
            style={{
              background: 'rgba(16,26,51,.85)',
              border: '1px solid rgba(255,255,255,.10)',
              backdropFilter: 'blur(10px)'
            }}
          >
            <FaTimes size={14} />
          </button>
        )}

        <div className="flex-shrink-0 pt-4 pb-6 px-4">
          <div
            className="relative flex items-center gap-3 rounded-2xl px-3 py-2 overflow-hidden"
            style={{
              background: '#111C34',
              border: '1px solid rgba(255,255,255,.08)',
              boxShadow: 'inset 0 0 0 1px rgba(255,255,255,.02)'
            }}
          >
            <img
              src="/club.png"
              alt="Leo Club"
              className="h-7 w-7 rounded select-none"
              onError={(e) => { e.currentTarget.src = '/default-logo.png'; }}
              draggable="false"
            />
            <div className="relative h-5 overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.span
                  key={titleCycle[titleIndex]}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.45, ease: 'easeOut' }}
                  className="font-semibold text-[14px] tracking-wide whitespace-nowrap text-white"
                >
                  {titleCycle[titleIndex]}
                </motion.span>
              </AnimatePresence>
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 pb-2 pt-1">
          <ul className="space-y-1">
            {menuItemsWithState.map((item, index) => {
              if (item.separator) {
                return (
                  <li key={`sep-${index}`} className="my-2">
                    <hr className="border-white/10" />
                  </li>
                );
              }
              const isActive = item.isActive;
              const hasChildren = item.children && item.children.length > 0;
              const isExpanded = !!expandedItems[item.label];

              return (
                <li key={item.label}>
                  <motion.div
                    whileHover={{ x: 4 }}
                    whileTap={{ scale: 0.98 }}
                    className={`group relative px-3 py-2.5 rounded-xl flex items-center justify-between cursor-pointer ${
                      isActive ? 'text-white' : 'text-gray-300 hover:text-white'
                    }`}
                    style={{
                      background: isActive ? '#132043' : 'transparent',
                      border: isActive ? '1px solid rgba(129,140,248,.35)' : '1px solid transparent',
                      boxShadow: isActive
                        ? '0 10px 24px rgba(0,0,0,.35), inset 0 0 0 1px rgba(129,140,248,.25)'
                        : 'none'
                    }}
                    onClick={() => {
                      if (hasChildren) {
                        toggleExpand(item.label);
                      } else {
                        navigate(item.route);
                        if (isMobile) setIsOpen(false);
                      }
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`text-lg ${isActive ? 'text-indigo-300' : 'text-gray-400 group-hover:text-indigo-200'}`}>
                        {item.icon}
                      </span>
                      <span className="text-sm font-medium">{item.label}</span>
                    </div>

                    <div className="flex items-center">
                      {item.badge !== null && item.badge !== undefined && (
                        <span className="bg-indigo-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full mr-2 shadow-[0_4px_14px_rgba(79,70,229,0.5)]">
                          {item.badge}
                        </span>
                      )}
                      {hasChildren && (
                        <motion.span
                          animate={{ rotate: isExpanded ? 0 : -90 }}
                          transition={{ duration: 0.2 }}
                          className="text-gray-400"
                        >
                          <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.08 1.04l-4.25 4.25a.75.75 0 01-1.06 0L5.21 8.27a.75.75 0 01.02-1.06z"/>
                          </svg>
                        </motion.span>
                      )}
                    </div>
                  </motion.div>

                  <AnimatePresence>
                    {hasChildren && isExpanded && (
                      <motion.ul
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="ml-8 mt-1 space-y-1 overflow-hidden"
                      >
                        {item.children.map((child) => {
                          const isChildActive = location.pathname === child.route;
                          return (
                            <motion.li
                              key={child.label}
                              whileHover={{ x: 3 }}
                              className={`px-3 py-1.5 text-sm rounded-md cursor-pointer ${
                                isChildActive ? 'text-indigo-300 font-medium' : 'text-gray-400 hover:text-white'
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(child.route);
                                if (isMobile) setIsOpen(false);
                              }}
                            >
                              {child.label}
                            </motion.li>
                          );
                        })}
                      </motion.ul>
                    )}
                  </AnimatePresence>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="mt-auto px-4 pb-3 pt-3 border-t border-white/10">
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl text-red-300 hover:text-red-200 transition"
            style={{ background: '#101A33' }}
            title="Logout"
          >
            <span className="flex items-center gap-3">
              <span className="text-lg"><FaPowerOff /></span>
              <span className="text-sm font-semibold">Logout</span>
            </span>
          </button>

          <div style={{ marginTop: 6, textAlign: 'center' }}>
            <span
              style={{
                fontSize: '12px',
                lineHeight: 1,
                color: 'rgba(255,255,255,0.38)',
                letterSpacing: '0.06em',
                display: 'inline-block',
                userSelect: 'none'
              }}
            >
              Powered by <span style={{ fontWeight: 600, color: 'rgba(255,255,255,0.48)' }}>SLIIT Leos</span>
            </span>
          </div>
        </div>

      </motion.aside>
    </>
  );
};

export default React.memo(Sidebar);
